import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import { X, ArrowRight, Sparkles } from "lucide-react";

const SESSION_KEY = "vw_exit_intent_shown";
const DISMISS_KEY = "vw_exit_intent_dismissed";

/**
 * Phase 8.3 — Exit intent overlay for the logged-out storefront only.
 *
 *  - Trigger: cursor leaves the viewport via the top edge (clientY <= 0).
 *  - One-shot per session via sessionStorage.
 *  - 7-day cool-down on dismiss via localStorage (`vw_exit_intent_dismissed` per tenant).
 *  - Skipped on coarse pointer (mobile/tablet) — `pointer: fine` only.
 *  - Soft entrance (200ms fade + scale).
 *
 * Caller must mount this only on logged-out, public storefront routes.
 */
export default function ExitIntentModal({
  tenantSlug,
  tenantName,
  cheapestPriceLabel = "",
  themeColor = "#7C3AED",
  accentColor = "#F59E0B",
}) {
  const navigate = useNavigate();
  const { sp } = useStore();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [enter, setEnter] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip on coarse pointer (mobile / tablet)
    if (!window.matchMedia?.("(pointer: fine)")?.matches) return;

    // One-shot per browser session
    try {
      if (sessionStorage.getItem(SESSION_KEY) === tenantSlug) return;
    } catch (e) { /* ignore */ }

    // 7-day dismiss cooldown
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.tenant === tenantSlug && parsed?.until > Date.now()) return;
      }
    } catch (e) { /* ignore */ }

    const onLeave = (e) => {
      if (open) return;
      // Only fire when cursor crosses the TOP edge (toward address bar / close tab).
      if (e.clientY > 0) return;
      // Ignore drag-and-drop / window-blur cases when relatedTarget is set.
      if (e.relatedTarget) return;
      setOpen(true);
      try { sessionStorage.setItem(SESSION_KEY, tenantSlug); } catch (err) { /* ignore */ }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [tenantSlug, open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setEnter(true), 20);
    return () => clearTimeout(t);
  }, [open]);

  const close = (persistDismiss = false) => {
    setEnter(false);
    setTimeout(() => setOpen(false), 200);
    if (persistDismiss) {
      try {
        localStorage.setItem(
          DISMISS_KEY,
          JSON.stringify({ tenant: tenantSlug, until: Date.now() + 7 * 24 * 60 * 60 * 1000 }),
        );
      } catch (e) { /* ignore */ }
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setError("Please enter a valid email");
      return;
    }
    setError("");
    const q = new URLSearchParams({ email: v, intent: "exit_intent" }).toString();
    navigate(sp(`/signup?${q}`));
  };

  if (!open) return null;
  return (
    <div
      className={`fixed inset-0 z-[60] grid place-items-center bg-black/65 backdrop-blur-sm transition-opacity duration-200 ${enter ? "opacity-100" : "opacity-0"}`}
      onClick={() => close(true)}
      data-testid="exit-intent-modal"
    >
      <div
        className={`relative w-full max-w-[480px] mx-4 transition-all duration-200 ${enter ? "scale-100" : "scale-95"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-card"
      >
        <div
          className="absolute -top-px left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }}
        />
        <div className="bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/10 p-7 md:p-9 relative">
          <button
            type="button"
            onClick={() => close(true)}
            className="absolute top-3 right-3 text-white/55 hover:text-white grid place-items-center w-8 h-8"
            aria-label="Dismiss"
            data-testid="exit-intent-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
            <span className="text-[10px] uppercase tracking-[0.4em] text-white/55">{tenantName}</span>
          </div>
          <h2 className="font-display font-black text-2xl md:text-4xl tracking-tighter leading-[1.05]" data-testid="exit-intent-headline">
            Wait — your story starts here.
          </h2>
          <p className="mt-3 text-white/65 text-sm md:text-base font-light">
            Join {tenantName} for {cheapestPriceLabel || "an indie-friendly price"}. Cancel anytime.
          </p>
          <form onSubmit={submit} className="mt-5 flex flex-col md:flex-row gap-2" data-testid="exit-intent-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
              placeholder="Email address"
              className="flex-1 h-12 bg-black/40 border border-white/20 px-4 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white"
              data-testid="exit-intent-email"
              autoFocus
            />
            <button
              type="submit"
              className="h-12 px-5 inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: accentColor, color: "#0a0a0a" }}
              data-testid="exit-intent-submit"
            >
              Start Watching <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          {error && <div className="mt-2 text-[11px] text-rose-300">{error}</div>}
          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => close(true)}
              className="text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-white"
              data-testid="exit-intent-maybe-later"
            >
              Maybe later
            </button>
          </div>
        </div>
        <div
          className="absolute -bottom-px left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)` }}
        />
      </div>
    </div>
  );
}
